/**
 * Data layer for `pricing-page`. Thin typed fetch wrappers over /api/v1/pricing-page.
 * Raw `fetch()` is confined to this file per the dash contract.
 */

export interface PricingPageRow {
	id: string;
	tenant_id: string;
	created_at: number;
	updated_at: number;
	[key: string]: unknown;
}

export interface ListPricingPageResponse {
	count: number;
	items: PricingPageRow[];
}

export interface CreatePricingPageBody {
	[key: string]: unknown;
}

const BASE = "/api/v1/pricing-page";

async function parseOrThrow<T>(res: Response): Promise<T> {
	const text = await res.text();
	let data: unknown = null;
	if (text) {
		try {
			data = JSON.parse(text);
		} catch {
			data = text;
		}
	}
	if (!res.ok) {
		const msg =
			data &&
			typeof data === "object" &&
			data !== null &&
			"error" in data &&
			typeof (data as { error: unknown }).error === "string"
				? (data as { error: string }).error
				: `HTTP ${res.status}`;
		throw new Error(msg);
	}
	return data as T;
}

export async function list(): Promise<ListPricingPageResponse> {
	const res = await fetch(BASE, {
		method: "GET",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<ListPricingPageResponse>(res);
}

export async function create(body: CreatePricingPageBody): Promise<PricingPageRow> {
	const res = await fetch(BASE, {
		method: "POST",
		credentials: "include",
		headers: {
			accept: "application/json",
			"content-type": "application/json",
		},
		body: JSON.stringify(body ?? {}),
	});
	return parseOrThrow<PricingPageRow>(res);
}

export async function remove(id: string): Promise<{ removed: boolean }> {
	const res = await fetch(`${BASE}/${encodeURIComponent(id)}`, {
		method: "DELETE",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<{ removed: boolean }>(res);
}

export const pricingPageQueryKeys = {
	all: ["pricing-page"] as const,
	list: () => [...pricingPageQueryKeys.all] as const,
	detail: (id: string) => [...pricingPageQueryKeys.all, id] as const,
};
